import { List, Map, Record } from 'immutable';
import { FAILURE } from '../../modules/bets/actions';
import { SUCCESS as CASH_IN_VALUE_SUCCESS } from '../../modules/cashInPrices/actions';
import { liveEventsReducer } from '../../modules/events/reducer';
import { UPDATE_PERIOD, UPDATE_SCORE, UPDATE_TIME } from '../../modules/livescore/actions';
import { EVENT_IN_PLAY_CHANGE } from '../../modules/pds/actions';
import {
  LOAD_MORE,
  SET_BETS,
  SET_CURRENT_BET,
  SET_CURRENT_SELECTION,
  SET_NEXT_BET,
  SET_PREV_BET,
  SET_VIEW_IS_LOADING,
  SHOW_CONGRATULATIONS,
  SHOW_LIST,
  UPDATE_BET_CASH_OUT_STATUS,
  UPDATE_VIEW_STATE
} from './actions';

export const Entities = new Record({
  bets: new Map(),
  selections: new Map(),
  events: new Map(),
  markets: new Map()
});

export const OpenBet = new Record({
  betId: '',
  betPlacedTime: '',
  isoBetPlacedTime: '',
  betPotentialPayout: '',
  betPayoutFormatted: '0.00',
  betPayoutValue: 0,
  betStake: 0,
  betStakePerLine: 0,
  betType: '',
  betStatus: '',
  slipId: '',
  selections: new List(),
  events: new List(),
  betCashInPrice: '',
  betCashOutStatus: '',
  fundingType: '',
  freeBet: false
});

export const Selection = new Record({
  selectionUniqueId: '',
  selectionId: 0,
  selectionLabel: '',
  selectionResult: '',
  selectionLiveId: '',
  marketId: '',
  eventId: ''
});

export const Event = new Record({
  eventId: '',
  eventName: '',
  eventStartDateTime: '',
  eventInPlay: false,
  eventIsFinished: false,
  eventLiveTime: '',
  eventPeriod: '',
  scoreHome: '',
  scoreAway: '',
  scoreLabel: '',
  subScoreHome: '',
  subScoreAway: '',
  subScoreLabel: '',
  sportName: ''
});

export const Market = new Record({
  marketId: '',
  marketLiveId: '',
  marketName: ''
});

export const InitialState = new Record({
  entities: new Entities(),
  betsIds: new List(),
  currentSelectionId: '',
  currentBetId: null,
  cashedOutPrices: new Map(),
  showList: true,
  viewIsLoading: false,
  loadingMore: false,
  nextSlip: null
});

const mapBets = bet => [bet.betId, new OpenBet({
  betId: bet.betId,
  betPlacedTime: bet.betPlacedTime,
  isoBetPlacedTime: bet.isoBetPlacedTime,
  betCashedInTime: bet.betCashedInTime,
  betPotentialPayout: bet.betPotentialPayout,
  betPayoutFormatted: bet.betPayoutFormatted,
  betPayoutValue: bet.betPayoutValue,
  betStake: bet.betStake,
  betStakePerLine: bet.betStakePerLine,
  betType: bet.betType,
  betStatus: bet.betStatus,
  slipId: bet.slipId,
  fundingType: bet.fundingType,
  freeBet: bet.freeBet,
  selections: new List(bet.selections.map(selection => selection.selectionUniqueId)),
  events: new List(bet.selections.map(selection => selection.eventId))
})];

const mapSelections = selection => [selection.selectionUniqueId, new Selection({
  selectionUniqueId: selection.selectionUniqueId,
  selectionId: selection.selectionId,
  selectionLiveId: selection.selectionLiveId,
  selectionLabel: selection.selectionLabel,
  selectionResult: selection.selectionResult === '-' ? '' : selection.selectionResult,
  marketId: selection.marketId,
  eventId: selection.eventId
})];

const mapEvents = selection => [selection.eventId, new Event({
  eventId: selection.eventId,
  eventName: selection.eventName || `${selection.eventHome} v ${selection.eventAway}`,
  eventStartDateTime: selection.eventStartDateTime,
  sportName: selection.sportName
})];

const mapMarkets = selection => [selection.marketId, new Market({
  marketId: selection.marketId,
  marketLiveId: selection.marketLiveId,
  marketName: selection.marketName
})];

const reduceBetsToSelections = (selections, bet) => selections.concat(bet.selections);

const makeSelectionUniqueIds = bet => {
  const b = bet;

  b.selections = bet.selections.map(selection => {
    const sel = selection;
    sel.selectionUniqueId = b.betId + sel.selectionId;
    return sel;
  });

  return b;
};

const setCurrentBet = (state, id) => (
  state
    .set('currentBetId', id)
    .set('showList', false)
);

export default (state = new InitialState(), action) => {
  const { type } = action;
  switch (type) {
    case SET_BETS: {
      const { results: { bets, nextSlip }, incremental } = action;
      const openResults = bets.filter(bet => !bet.betSettled).map(makeSelectionUniqueIds);
      const betsIds = openResults.map(bet => bet.betId);
      const betsEntities = openResults.map(mapBets);
      const selectionsEntities = openResults.reduce(reduceBetsToSelections, []).map(mapSelections);
      const eventsEntities = openResults.reduce(reduceBetsToSelections, []).map(mapEvents);
      const marketsEntities = openResults.reduce(reduceBetsToSelections, []).map(mapMarkets);

      if (incremental) {
        return state.mergeDeep({
          entities: new Entities({
            bets: new Map(betsEntities),
            selections: new Map(selectionsEntities),
            events: new Map(eventsEntities),
            markets: new Map(marketsEntities)
          }),
          betsIds: state.betsIds.concat(betsIds.filter(betId => !state.betsIds.includes(betId))),
          loadingMore: false,
          nextSlip
        });
      }

      return state.merge({
        entities: new Entities({
          bets: new Map(betsEntities),
          selections: new Map(selectionsEntities),
          events: new Map(eventsEntities),
          markets: new Map(marketsEntities)
        }),
        betsIds: new List(betsIds),
        loadingMore: false,
        nextSlip
      });
    }
    case SET_CURRENT_BET: {
      const { id } = action;
      return setCurrentBet(state, id);
    }
    case SET_PREV_BET: {
      const { currentBetId } = state;
      const currentBetIndex = state.betsIds.indexOf(currentBetId);
      return setCurrentBet(state, state.betsIds.get(currentBetIndex - 1));
    }
    case SET_NEXT_BET: {
      const { currentBetId } = state;
      const currentBetIndex = state.betsIds.indexOf(currentBetId);
      return setCurrentBet(state, state.betsIds.get(currentBetIndex + 1));
    }
    case SET_CURRENT_SELECTION: {
      const { id } = action;
      return state.set('currentSelectionId', id);
    }
    case SHOW_LIST: {
      return state.set('showList', true);
    }
    case UPDATE_VIEW_STATE: {
      const { currentBetId, showList } = action;
      return state.merge({
        currentBetId,
        showList
      });
    }
    case CASH_IN_VALUE_SUCCESS: {
      const { results } = action;
      let newState = state;
      results.forEach(result => {
        if (state.entities.bets.has(result.betId)
          && (result.betCashInPrice || result.betCashOutStatus)) {
          newState = newState.mergeIn(['entities', 'bets', result.betId], {
            betCashInPrice: result.betCashInPrice,
            betCashOutStatus: result.betCashOutStatus
          });
        }
      });

      return newState;
    }
    case SHOW_CONGRATULATIONS: {
      const { id, price } = action;

      return state.setIn(['cashedOutPrices', id], price);
    }
    case EVENT_IN_PLAY_CHANGE:
    case UPDATE_TIME:
    case UPDATE_PERIOD:
    case UPDATE_SCORE: {
      return liveEventsReducer(state, action);
    }
    case UPDATE_BET_CASH_OUT_STATUS: {
      const { id, status } = action;
      return state.setIn(['entities', 'bets', id, 'betStatus'], status);
    }
    case SET_VIEW_IS_LOADING: {
      return state.set('viewIsLoading', action.loading);
    }
    case LOAD_MORE: {
      return state.set('loadingMore', true);
    }
    case FAILURE: {
      return state.set('loadingMore', false);
    }
    default: {
      return state;
    }
  }
};
